import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks } from "./helpers";
import { vendorUUIDs } from "../../constants";

import FindGamesBanner from "public/images/hickorycrawdads/Crawdads-Attend-Banner.jpg";
import StaticSeatMap from "public/images/hickorycrawdads/LPFransStadiumSeatingMap2025Zones.jpg";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/hickory-crawdads.png";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "hickorycrawdads_flex",
  browserTitle: "Build Your Own Bundle - Flex",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Hickory Crawdads",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 10,
        customProgressBarMessage:
          "Add 2 more games and receive 15% off or 4 more games and receive 20% off your seats!",
      },
      {
        id: 2,
        minSelectedAmount: 4,
        updatedAccountGroup: 1,
        discount: 15,
        customProgressBarMessage:
          "Congrats! You have unlocked 15% off your seats! Add 2 more games for 20% off or checkout now.",
      },
      {
        id: 3,
        minSelectedAmount: 6,
        updatedAccountGroup: 2,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You have unlocked 20% off your seats! Add more games or checkout now.",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    isView3dButtonHidden: true,
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "80%",
      display: "block",
      marginX: "auto",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 32,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=DHCM_MYTIXX&orgid=57072#/auth/login?fullret=%2Fticketmanagement%2F%3Fagency%3DDHCM_MYTIXX%26orgid%3D57072",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: FindGamesBanner.src,
    checkoutStatusPageImage: FindGamesBanner.src,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideSectionFilter: false,
      hideAccessibilityFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: false,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:8283223000"
          sx={{
            color: "primary.main",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          (828) 322-3000
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#ec0928",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),
      progressBarText:
        "Add 2 games and get 10% off, add 4 games and get 15% off or add 6 games and get 20% off your seats.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Hickory Crawdads tickets are confirmed! Your tickets will be delivered to your Crawdads ProVenue account within 24 hours.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
